import type { AgentRuntimeSnapshot } from "@cord/shared"
import { sanitizeClaudeRuntimeSnapshot } from "@/lib/claude-runtime-snapshot-sanitization"
import { getForegroundOwnershipTabId } from "@/lib/session-foreground-ownership"
import type {
  ThreadSyncRenderSource,
  ThreadSyncResumeSource,
} from "@/lib/thread-sync-activation-contract"
import { toKnownAgentType, type ThreadControlPlaneState } from "@/lib/thread-sync-control-plane"
import {
  buildEmptyManagedRuntimeRawProjectionState,
  deriveExpectedRuntimeCursor,
  hasSerializableContinuityValidatedReplicaProjection,
  hasAnyManagedRuntimeRawProjection,
  hasConsistentContinuityValidatedCursor,
  normalizeManagedRuntimeState,
  resolveManagedRuntimeRawProjectionState,
  type ManagedRuntimeRawProjectionState,
  type ManagedRuntimeState,
} from "@/lib/thread-sync-replica-runtime"
import {
  buildThreadSyncReplicaProvenance,
  type ThreadSyncReplicaProvenance,
} from "@/lib/thread-sync-replica-provenance"
import {
  buildControlPlaneOnlyRuntimeSnapshot,
  isDisplayOnlyRuntimeMaterialization,
  isNonAuthoritativeRuntimeMaterialization,
  resolveCapturedRuntimeMetadata,
  type ThreadSyncReplicaQuality,
  type ThreadRuntimeMaterialization,
} from "@/lib/thread-sync-runtime-materialization"
import {
  areRuntimeSnapshotsEquivalent,
} from "@/lib/runtime-snapshot-stability"
import type { SyncReadyDebugSnapshot } from "@/lib/thread-sync-history-token-debug"
import { debugInfo, debugWarn } from "@/stores/debug-logs"
import { useAgentRuntime } from "@/stores/agent-runtime"
import type { PendingForkSnapshot } from "@/stores/pending-fork-snapshot"
import type { ThreadSyncResumeState } from "@/lib/thread-sync-resume-state"
import {
  clearThreadSyncPersistedReplicaSummaryPreservingInvalidation,
  markThreadSyncPersistedReplicaInvalidation,
} from "@/lib/thread-sync-replica-summary-store"

export type ThreadSyncRuntimeState = {
  cachedState: ManagedRuntimeState | null
  materialization: ThreadRuntimeMaterialization
  connectionVersion: number | null
  continuityValidatedConnectionVersion: number | null
  replicaQuality: ThreadSyncReplicaQuality | null
  replicaSavedAtMs: number | null
  replicaProvenance: ThreadSyncReplicaProvenance | null
  optimisticSeed: PendingForkSnapshot | null
  renderSource: ThreadSyncRenderSource
  resumeSource: ThreadSyncResumeSource
  lastApplyError: { reason: string } | null
  lastSyncReadyDecision: SyncReadyDebugSnapshot | null
  lastReset: SyncReadyDebugSnapshot | null
}

export type PreparedRuntimeApplyState = {
  runtimeState: ManagedRuntimeState | null
  materialization: ThreadRuntimeMaterialization
  runtimeConnectionVersion: number | null
  continuityValidatedConnectionVersion: number | null
  replicaQuality: ThreadSyncReplicaQuality | null
  replicaSavedAtMs: number | null
  replicaProvenance: ThreadSyncReplicaProvenance | null
  renderSource: ThreadSyncRenderSource
  resumeSource: ThreadSyncResumeSource
}

const replicaInvalidationClockBySessionId = new Map<string, number>()

function reserveReplicaInvalidationTimestamp(args: {
  sessionId: string
  requestedNowMs?: number
}): number {
  const currentReservedNowMs = replicaInvalidationClockBySessionId.get(args.sessionId) ?? 0
  if (
    typeof args.requestedNowMs === "number" &&
    Number.isFinite(args.requestedNowMs) &&
    args.requestedNowMs > 0
  ) {
    const normalizedRequestedNowMs = Math.floor(args.requestedNowMs)
    const resolvedNowMs = Math.max(currentReservedNowMs, normalizedRequestedNowMs)
    replicaInvalidationClockBySessionId.set(args.sessionId, resolvedNowMs)
    return resolvedNowMs
  }

  const reservedNowMs = Math.max(
    Date.now(),
    currentReservedNowMs + 1,
  )
  replicaInvalidationClockBySessionId.set(args.sessionId, reservedNowMs)
  return reservedNowMs
}

export function createThreadSyncRuntimeState(
  optimisticSeed?: PendingForkSnapshot | null,
): ThreadSyncRuntimeState {
  return {
    cachedState: null,
    materialization: "empty",
    connectionVersion: null,
    continuityValidatedConnectionVersion: null,
    replicaQuality: null,
    replicaSavedAtMs: null,
    replicaProvenance: null,
    optimisticSeed: optimisticSeed ?? null,
    renderSource: "empty",
    resumeSource: "none",
    lastApplyError: null,
    lastSyncReadyDecision: null,
    lastReset: null,
  }
}

function buildManagedRuntimeStateFromActiveStore(): ManagedRuntimeState {
  const state = useAgentRuntime.getState()
  const snapshot = state.agentType === "claude_code"
    ? sanitizeClaudeRuntimeSnapshot({ snapshot: state.snapshot })
    : state.snapshot

  return normalizeManagedRuntimeState({
    runtimeState: {
      agentType: state.agentType,
      snapshot,
      lastAppliedTimestamp: state.lastAppliedTimestamp,
      lastEventId: state.lastEventId,
      rawOpenCodeProjection: state.rawOpenCodeProjection,
      rawClaudeProjection: state.rawClaudeProjection,
      rawCursorProjection: state.rawCursorProjection,
    },
  })
}

function areRuntimeSnapshotsEquivalentForRawProjectionReuse(args: {
  left: AgentRuntimeSnapshot | null
  right: AgentRuntimeSnapshot | null
}): boolean {
  return areRuntimeSnapshotsEquivalent(args)
}

function reuseCompatibleRawProjectionFallback(args: {
  nextRuntimeState: ManagedRuntimeState
  previousRuntimeState: ManagedRuntimeState | null
  resume: Pick<ThreadSyncResumeState, "cursor">
}): ManagedRuntimeRawProjectionState | null {
  const nextRawProjectionState = resolveManagedRuntimeRawProjectionState(args.nextRuntimeState)
  if (hasAnyManagedRuntimeRawProjection(nextRawProjectionState)) {
    return null
  }

  const previousRuntimeState = args.previousRuntimeState
  if (!previousRuntimeState || previousRuntimeState.agentType !== args.nextRuntimeState.agentType) {
    return null
  }

  const previousRawProjectionState = resolveManagedRuntimeRawProjectionState(previousRuntimeState)
  if (!hasAnyManagedRuntimeRawProjection(previousRawProjectionState)) {
    return null
  }

  const expectedCursor = deriveExpectedRuntimeCursor({
    runtimeState: args.nextRuntimeState,
    resume: args.resume,
  })
  const previousCursor = deriveExpectedRuntimeCursor({
    runtimeState: previousRuntimeState,
    resume: args.resume,
  })

  if (expectedCursor !== previousCursor) {
    return null
  }

  if (!areRuntimeSnapshotsEquivalentForRawProjectionReuse({
    left: args.nextRuntimeState.snapshot,
    right: previousRuntimeState.snapshot,
  })) {
    return null
  }

  return previousRawProjectionState
}

function buildForegroundReplicaProvenance(args?: {
  sourceKind?: "foreground_ws" | "foreground_http_prefill"
}): ThreadSyncReplicaProvenance {
  return buildThreadSyncReplicaProvenance({
    sourceKind: args?.sourceKind ?? "foreground_ws",
    sourceTabId: getForegroundOwnershipTabId(),
  })
}

function resolveThreadSyncReplicaQualityForRuntime(args: {
  runtime: ThreadSyncRuntimeState
  runtimeState: ManagedRuntimeState
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  materialization: ThreadRuntimeMaterialization
}): ThreadSyncReplicaQuality | null {
  if (
    args.materialization !== "authoritative" &&
    args.materialization !== "authoritative_partial"
  ) {
    return null
  }

  if (
    args.materialization === "authoritative" &&
    args.runtime.continuityValidatedConnectionVersion === args.runtime.connectionVersion &&
    typeof args.resume.historyToken === "string" &&
    args.resume.historyToken.trim().length > 0 &&
    hasConsistentContinuityValidatedCursor({
      runtimeState: args.runtimeState,
      resume: args.resume,
    }) &&
    hasSerializableContinuityValidatedReplicaProjection(args.runtimeState)
  ) {
    return "continuity_validated"
  }

  return "best_effort"
}

function resolveThreadSyncReplicaProvenanceForRuntime(args: {
  runtime: ThreadSyncRuntimeState
  materialization: ThreadRuntimeMaterialization
  explicitProvenance?: ThreadSyncReplicaProvenance | null
}): ThreadSyncReplicaProvenance | null {
  if (args.explicitProvenance) {
    return args.explicitProvenance
  }

  if (
    args.materialization === "authoritative" ||
    args.materialization === "authoritative_partial"
  ) {
    if (
      args.runtime.replicaProvenance !== null &&
      args.runtime.replicaProvenance.sourceKind !== "hydrated"
    ) {
      return args.runtime.replicaProvenance
    }

    return buildForegroundReplicaProvenance()
  }

  return args.runtime.replicaProvenance
}

function buildRuntimeSnapshotDebugSummary(args: {
  runtimeState: ManagedRuntimeState | null
}): Record<string, unknown> {
  const snapshot = args.runtimeState?.snapshot ?? null
  const openCodeProjection = args.runtimeState?.rawOpenCodeProjection ?? null
  const claudeProjection = args.runtimeState?.rawClaudeProjection ?? null

  return {
    hasSnapshot: snapshot !== null,
    lastEventId: args.runtimeState?.lastEventId ?? null,
    timelineCount: snapshot?.timeline.length ?? 0,
    pendingInteractionCount: snapshot?.pendingInteractions.length ?? 0,
    pendingMessageCount: snapshot?.pendingMessages.length ?? 0,
    conversationEpoch: snapshot?.conversationEpoch ?? null,
    providerEventId: snapshot?.cursorState.providerEventId ?? null,
    agentType: args.runtimeState?.agentType ?? null,
    hasOpenCodeProjection: openCodeProjection !== null,
    openCodeMessageCount: Array.isArray(openCodeProjection?.orderedMessageIds)
      ? openCodeProjection.orderedMessageIds.length
      : Object.keys(openCodeProjection?.messagesById ?? {}).length,
    openCodeChildSessionCount: Array.isArray(openCodeProjection?.orderedChildSessionIds)
      ? openCodeProjection.orderedChildSessionIds.length
      : Object.keys(openCodeProjection?.childSessionStateBySessionId ?? {}).length,
    hasClaudeProjection: claudeProjection !== null,
    claudeEventCount: claudeProjection?.events.length ?? 0,
    hasCursorProjection: args.runtimeState?.rawCursorProjection !== null,
  }
}

export function restoreRuntimeState(runtime: ThreadSyncRuntimeState): boolean {
  if (!runtime.cachedState) return false
  const cachedState = normalizeManagedRuntimeState({
    runtimeState: runtime.cachedState,
  })
  runtime.cachedState = cachedState
  const snapshot = cachedState.agentType === "claude_code"
    ? sanitizeClaudeRuntimeSnapshot({ snapshot: cachedState.snapshot })
    : cachedState.snapshot
  useAgentRuntime.setState((state) => ({
    agentType: cachedState.agentType,
    snapshot,
    lastAppliedTimestamp: cachedState.lastAppliedTimestamp,
    lastEventId: cachedState.lastEventId,
    rawOpenCodeProjection: cachedState.rawOpenCodeProjection,
    rawClaudeProjection: cachedState.rawClaudeProjection,
    rawCursorProjection: cachedState.rawCursorProjection,
    stateVersion: state.stateVersion + 1,
  }))
  return true
}

export function resetRuntimeStore(): void {
  useAgentRuntime.getState().reset()
}

export function clearRuntimeState(runtime: ThreadSyncRuntimeState): void {
  runtime.cachedState = null
  runtime.materialization = "empty"
  runtime.connectionVersion = null
  runtime.continuityValidatedConnectionVersion = null
  runtime.replicaQuality = null
  runtime.replicaSavedAtMs = null
  runtime.replicaProvenance = null
  runtime.optimisticSeed = null
  runtime.renderSource = "empty"
  runtime.resumeSource = "none"
  runtime.lastApplyError = null
}

export function resetRuntimeStateAndInvalidatePersistedReplica(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
}): number | null {
  if (args.activeThreadId !== args.sessionId) {
    return null
  }
  resetRuntimeStore()
  clearRuntimeState(args.runtime)
  return invalidatePersistedReplicaState({
    sessionId: args.sessionId,
  })
}

export function invalidatePersistedReplicaState(args: {
  sessionId: string
  requestedNowMs?: number
}): number {
  clearThreadSyncPersistedReplicaSummaryPreservingInvalidation(args.sessionId)
  const invalidatedAtMs = reserveReplicaInvalidationTimestamp({
    sessionId: args.sessionId,
    requestedNowMs: args.requestedNowMs,
  })
  markThreadSyncPersistedReplicaInvalidation({
    sessionId: args.sessionId,
    invalidatedAtMs,
  })
  return invalidatedAtMs
}

export function invalidatePersistedReplicaBySessionId(sessionId: string): void {
  const invalidatedAtMs = reserveReplicaInvalidationTimestamp({
    sessionId,
  })
  markThreadSyncPersistedReplicaInvalidation({
    sessionId,
    invalidatedAtMs,
  })
}

export function captureActiveRuntimeState(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  materialization?: Exclude<ThreadRuntimeMaterialization, "empty">
  provenance?: ThreadSyncReplicaProvenance | null
  nowMs?: number
}): void {
  const runtimeState = syncActiveRuntimeStateToCache(args)
  if (runtimeState === null) {
    return
  }

  if (runtimeState.snapshot === null) {
    clearRuntimeState(args.runtime)
    invalidatePersistedReplicaState({
      sessionId: args.sessionId,
    })
    return
  }
}

export function syncActiveRuntimeStateToCache(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  materialization?: Exclude<ThreadRuntimeMaterialization, "empty">
  provenance?: ThreadSyncReplicaProvenance | null
  nowMs?: number
}): ManagedRuntimeState | null {
  const previousRuntimeState = args.runtime.cachedState
  const runtimeState = buildManagedRuntimeStateFromActiveStore()
  if (args.activeThreadId !== args.sessionId) {
    return null
  }
  const rawProjectionFallback = reuseCompatibleRawProjectionFallback({
    nextRuntimeState: runtimeState,
    previousRuntimeState,
    resume: args.resume,
  })

  args.runtime.cachedState = rawProjectionFallback
    ? {
        ...runtimeState,
        ...rawProjectionFallback,
      }
    : runtimeState
  const runtimeMetadata = resolveCapturedRuntimeMetadata({
    currentMaterialization: args.runtime.materialization,
    explicitMaterialization: args.materialization,
    currentRuntimeConnectionVersion: args.runtime.connectionVersion,
    connectionVersion: args.connectionVersion,
  })
  args.runtime.materialization = runtimeMetadata.materialization
  args.runtime.connectionVersion = runtimeMetadata.runtimeConnectionVersion
  args.runtime.replicaQuality = resolveThreadSyncReplicaQualityForRuntime({
    runtime: args.runtime,
    runtimeState: args.runtime.cachedState,
    resume: args.resume,
    materialization: runtimeMetadata.materialization,
  })
  args.runtime.replicaSavedAtMs = args.nowMs ?? Date.now()
  args.runtime.replicaProvenance = resolveThreadSyncReplicaProvenanceForRuntime({
    runtime: args.runtime,
    materialization: runtimeMetadata.materialization,
    explicitProvenance: args.provenance,
  })

  return runtimeState
}

export function replaceDisplayOnlyRuntimeWithControlPlaneOnly(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  controlPlane: Pick<
    ThreadControlPlaneState,
    "agentType" | "sessionStatus" | "devServerError" | "conversationEpoch" | "runtimeSnapshot"
  >
  codeServerError: string | null
  connectionVersion: number
  nowMs?: number
}): boolean {
  if (
    !isDisplayOnlyRuntimeMaterialization(args.runtime.materialization) ||
    args.activeThreadId !== args.sessionId
  ) {
    return false
  }

  const currentSnapshot = useAgentRuntime.getState().snapshot
  if (!currentSnapshot) {
    useAgentRuntime.getState().reset()
    clearRuntimeState(args.runtime)
    invalidatePersistedReplicaState({
      sessionId: args.sessionId,
    })
    return true
  }

  const controlPlaneOnlySnapshot: AgentRuntimeSnapshot = {
    ...buildControlPlaneOnlyRuntimeSnapshot({
      currentSnapshot,
      controlPlaneAgentType: toKnownAgentType(args.controlPlane.agentType),
      sessionStatus: args.controlPlane.sessionStatus,
      controlPlaneDevServerError: args.controlPlane.devServerError,
      codeServerError: args.codeServerError,
      controlPlaneConversationEpoch: args.controlPlane.conversationEpoch,
      runtimeSnapshot: args.controlPlane.runtimeSnapshot,
    }),
  }

  useAgentRuntime.setState((state) => ({
    agentType: useAgentRuntime.getState().agentType,
    snapshot: controlPlaneOnlySnapshot,
    lastAppliedTimestamp: null,
    lastEventId: null,
    rawOpenCodeProjection: null,
    rawClaudeProjection: null,
    rawCursorProjection: null,
    stateVersion: state.stateVersion + 1,
  }))

  debugInfo("sync.new", "Replaced display-only restore with control-plane shell", {
    sessionId: args.sessionId,
    previousMaterialization: args.runtime.materialization,
    cursor: args.resume.cursor,
    historyToken: args.resume.historyToken,
    controlPlaneSessionStatus: args.controlPlane.sessionStatus,
    controlPlaneConversationEpoch: args.controlPlane.conversationEpoch,
    ...buildRuntimeSnapshotDebugSummary({
      runtimeState: {
        agentType: useAgentRuntime.getState().agentType,
        snapshot: controlPlaneOnlySnapshot,
        lastAppliedTimestamp: null,
        lastEventId: null,
        rawOpenCodeProjection: null,
        rawClaudeProjection: null,
        rawCursorProjection: null,
      },
    }),
  })

  const coldResume = {
    cursor: 0,
    historyToken: null,
  } satisfies Pick<ThreadSyncResumeState, "cursor" | "historyToken">

  captureActiveRuntimeState({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resume: coldResume,
    materialization: "control_plane_only",
    nowMs: args.nowMs,
  })

  return true
}

export function prepareRuntimeForAuthoritativeApply(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
}): PreparedRuntimeApplyState | null {
  if (args.activeThreadId !== args.sessionId) {
    return null
  }

  const preparedState: PreparedRuntimeApplyState = {
    runtimeState: args.runtime.cachedState,
    materialization: args.runtime.materialization,
    runtimeConnectionVersion: args.runtime.connectionVersion,
    continuityValidatedConnectionVersion:
      args.runtime.continuityValidatedConnectionVersion,
    replicaQuality: args.runtime.replicaQuality,
    replicaSavedAtMs: args.runtime.replicaSavedAtMs,
    replicaProvenance: args.runtime.replicaProvenance,
    renderSource: args.runtime.renderSource,
    resumeSource: args.runtime.resumeSource,
  }

  if (
    isNonAuthoritativeRuntimeMaterialization(args.runtime.materialization) &&
    args.runtime.cachedState
  ) {
    resetRuntimeStore()
    clearRuntimeState(args.runtime)
  }

  return preparedState
}

export function restoreRuntimeAfterFailedApply(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  preparedState: PreparedRuntimeApplyState | null
}): void {
  if (!args.preparedState || args.activeThreadId !== args.sessionId) {
    return
  }

  if (!args.preparedState.runtimeState) {
    resetRuntimeStore()
    clearRuntimeState(args.runtime)
    return
  }

  args.runtime.cachedState = args.preparedState.runtimeState
  args.runtime.materialization = args.preparedState.materialization
  args.runtime.connectionVersion = args.preparedState.runtimeConnectionVersion
  args.runtime.continuityValidatedConnectionVersion =
    args.preparedState.continuityValidatedConnectionVersion
  args.runtime.replicaQuality = args.preparedState.replicaQuality
  args.runtime.replicaSavedAtMs = args.preparedState.replicaSavedAtMs
  args.runtime.replicaProvenance = args.preparedState.replicaProvenance
  args.runtime.renderSource = args.preparedState.renderSource
  args.runtime.resumeSource = args.preparedState.resumeSource
  restoreRuntimeState(args.runtime)
}

export function handleRuntimeApplyFailure(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  preparedState: PreparedRuntimeApplyState | null
  reason: string
}): void {
  restoreRuntimeAfterFailedApply({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    preparedState: args.preparedState,
  })
  args.runtime.lastApplyError = { reason: args.reason }
}

export function finalizeAuthoritativeRuntimeState(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  nowMs?: number
}): void {
  captureActiveRuntimeState({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resume: args.resume,
    materialization: "authoritative",
    nowMs: args.nowMs,
  })
}

export function capturePartialAuthoritativeRuntimeState(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  nowMs?: number
}): void {
  captureActiveRuntimeState({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resume: args.resume,
    materialization: "authoritative_partial",
    nowMs: args.nowMs,
  })
}

export function applyOptimisticRuntimeSeed(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  nowMs?: number
}): void {
  if (!args.runtime.optimisticSeed || args.activeThreadId !== args.sessionId) {
    return
  }
  const optimisticSeed = args.runtime.optimisticSeed
  args.runtime.renderSource = "optimistic_seed"
  args.runtime.resumeSource = "none"

  useAgentRuntime.setState((state) => ({
    agentType: optimisticSeed.agentType,
    snapshot: optimisticSeed.snapshot,
    lastAppliedTimestamp: null,
    lastEventId: null,
    rawOpenCodeProjection: null,
    rawClaudeProjection: null,
    rawCursorProjection: null,
    stateVersion: state.stateVersion + 1,
  }))

  captureActiveRuntimeState({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resume: args.resume,
    materialization: "optimistic_seed",
    nowMs: args.nowMs,
  })
}
